import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import {
    SharedModule,
    StringCustomFieldConfig,
    FormInputComponent,
    NotificationService,
    DataService,
    DynamicFormInputComponent
} from '@vendure/admin-ui/core';
import { Collection } from '../generated';
import { areArraysEqual, safeJSONParse } from '../utils';
import { forkJoin, Observable, of } from 'rxjs';
import { CollectionCarouselData, ID, TextAlignment } from '../ui-types';

@Component({
  selector: 'collection-carousel-form',
  template: `
    <vdr-card>
      <div>
        <!-- Text Alignment -->
        <vdr-form-field [label]="'Text Alignment'">
          <select [formControl]="textAlignmentControl">
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </vdr-form-field>

        <!-- Mobile Style -->
        <vdr-form-field [label]="'Mobile Style'">
          <select [formControl]="mobileStyleControl">
            <option value="slide">Slide</option>
            <option value="tiles">Tiles</option>
          </select>
        </vdr-form-field>

        <!-- Compact Row -->
        <div class="form-row">
          <!-- Collection Selectors -->
          <vdr-form-field [label]="'Collection'" class="form-item">
            <vdr-dynamic-form-input #collectionsInput
              [control]="collectionsControl"
              [def]="{
                type: 'relation',
                list: true,
                entity: 'Collection',
                ui: {
                  component: 'relation-collection-selector',
                }
              }"
            >
            </vdr-dynamic-form-input>
          </vdr-form-field>
        </div>
      </div>
    </vdr-card>
  `,
  styleUrls: ['../styles.scss'],
  standalone: true,
  imports: [SharedModule],
})
export class CollectionCarouselFormComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit {
  @ViewChild('collectionsInput') collectionsInput: DynamicFormInputComponent;
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  collectionCarouselData: CollectionCarouselData;

  textAlignmentControl = new FormControl();
  mobileStyleControl = new FormControl();
  collectionsControl = new UntypedFormControl();

  constructor(
    private notificationService: NotificationService,
    private dataService: DataService
  ) {}

  ngOnInit() {
    const defaultCollectionCarouselData: CollectionCarouselData = {
      textAlignment: TextAlignment.Center,
      mobileStyle: 'tiles',
      collectionIds: [],
    };
    this.collectionCarouselData = safeJSONParse<CollectionCarouselData>(
      this.formControl?.value,
      this.notificationService,
      defaultCollectionCarouselData,
    )!;
    this.initializeFormControls();

    if (this.collectionCarouselData && this.collectionCarouselData.collectionIds && this.collectionCarouselData.collectionIds.length > 0 ) {
      this.loadCollectionsByIds(this.collectionCarouselData.collectionIds).subscribe((collections) => {
        this.collectionsControl.setValue(collections);
        if (this.collectionsInput) {
          this.collectionsInput.writeValue(collections);
        }
      });
    }
  }

  ngAfterViewInit() {
    this.textAlignmentControl.valueChanges.subscribe(this.onTextAlignmentChange);
    this.mobileStyleControl.valueChanges.subscribe(this.onMobileStyleChange);
    if (this.collectionsInput) {
        this.collectionsInput.registerOnChange(this.onCollectionsChange);
    }
  }

  private loadCollectionsByIds(ids: ID[]): Observable<Collection[]> {
    if (!ids || ids.length === 0) {
      return of([]);
    }
    return forkJoin(ids.map((id) => this.dataService.collection.getCollections({
      filter: {
        id: { eq: id.toString() }
      },
    }).mapSingle((data) => data.collections?.items[0]))) as Observable<Collection[]>;
  }

  private initializeFormControls() {
    this.textAlignmentControl.setValue(this.collectionCarouselData?.textAlignment || 'center');
    this.mobileStyleControl.setValue(this.collectionCarouselData?.mobileStyle);
  }

  private onTextAlignmentChange = (value: TextAlignment) => {
    if (this.collectionCarouselData?.textAlignment !== value) {
      this.collectionCarouselData!.textAlignment = value;
      this.updateJsonString();
    }
  };

  private onMobileStyleChange = (value: string) => {
    if (this.collectionCarouselData?.mobileStyle !== value) {
      this.collectionCarouselData!.mobileStyle = value;
      this.updateJsonString();
    }
  }

  private onCollectionsChange = (collections: Collection[]) => {
    const collectionIds = collections.map((collection) => collection.id);
    if (this.collectionCarouselData && !areArraysEqual(this.collectionCarouselData?.collectionIds||[], collectionIds)) {
      this.collectionCarouselData!.collectionIds = collectionIds;
      this.updateJsonString();
    }
  };

  private updateJsonString() {
    const jsonString = JSON.stringify(this.collectionCarouselData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
